import { Container } from "react-bootstrap";
import Counter from "./features/counter/Counter";
import axios from "axios";
import Header from "./components/Header";
import Footer from "./components/Footer";
import NotificationPage from "./components/Push_notification";
import { Link, useNavigate } from "react-router-dom";
import { Routes, Route, useLocation, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ReservationCustomersMenuScreen from "./screens/ReservationCustomersMenuScreen";
import ReservationFromRestaurantScreen from "./screens/ReservationFromRestaurantScreen";
import ReservationScreen from "./screens/ReservationScreen";
import ProfileScreen from "./screens/ProfileScreen";
import CreateAccountScreen from "./screens/CreateAccountScreen";
import HomeScreen from "./screens/HomeScreen";
import LoginScreen from "./screens/LoginScreen";
import RestaurantSettingsUpdate from "./screens/RestaurantSettingsUpdate";
import RestaurantZonesTablesManagement from "./screens/RestaurantZonesTablesManagement";
import RestaurantTimeManagement from "./screens/RestaurantTimeManagement";
import RestaurantCreation from "./screens/RestaurantCreation";
import RestaurantClients from "./screens/RestaurantClients";
import ManagersCreationScreen from "./screens/ManagersCreationScreen";
import PlaceDesignScreen from "./screens/PlaceDesignScreen";
import ConfirmNotification from "./screens/ConfirmNotification";
import Notifications from "./screens/Notifications";
import AccountRecovery from "./screens/AccountRecovery";
import PasswordRecoveryChange from "./screens/PasswordRecoveryChange";
import FiscalAdress from "./screens/FiscalAdress";
import ChoosePlanAndPay from "./screens/ChoosePlanAndPay";
import DeleteClientInfo from "./screens/DeleteClientInfo";
import RestaurantInfoUpdate from "./screens/RestaurantInfoUpdate";
import RestaurantStatistics from "./screens/RestaurantStatistics";
import SubscriptionManagement from "./screens/SubscriptionManagement";
import Time from "./screens/Time";
import styled from "styled-components";
import { useState, useEffect } from "react";
import ConnectionStatus from "./components/ConectionStatus";
import { logout, checkCurrentUserExists } from "./actions/userActions";
import RestaurantWidgetSettings from "./screens/RestaurantWidgetSettings";
import RestaurantSetupTour from "./components/RestaurantSetupTour";

import { fetchReservationActive } from "./actions/reservationsActions";

export const api = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// Every call carries the signed-in user's token. Most of the restaurant
// API used to answer anonymously, so many calls never attached one; the
// server now requires it (base/permissions.py). Guests have no userInfo
// and stay anonymous, and a call that sets its own Authorization keeps it.
api.interceptors.request.use(config => {
  const headers = config.headers || {};
  if (!headers.Authorization) {
    try {
      const userInfo = JSON.parse(localStorage.getItem("userInfo") || "null");
      if (userInfo && userInfo.token) {
        headers.Authorization = `Bearer ${userInfo.token}`;
        config.headers = headers;
      }
    } catch (error) {
      // Storage unreadable: the call goes out anonymously, as before.
    }
  }
  return config;
});

const StyledRoutesContainer = styled.div`
  max-height: 100dvh;
  height: ${({ $widgetMode }) => ($widgetMode ? "100dvh" : "auto")};
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: ${({ $widgetMode }) => ($widgetMode ? "0" : "0")};
  font-family: "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  overflow-y: auto;
  overflow-x: hidden;
  background: #ffffff;

  &::-webkit-scrollbar {
    width: ${({ $widgetMode }) => ($widgetMode ? "4px" : "8px")};
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #d8d3d1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #b9b2af;
  }

  @media (max-width: 768px) {
    padding-top: ${(props) => (props.noPadding ? "0" : "60px")};
  }
`;

const NO_PADDING_PATHS = [
  "/c",
  "/customer_reservation_menu",
  "/login",
  "/recovery_code",
  "/reservations",
  "/widget",
];

const FOOTER_ACTIVE_PATHS = [
  "/c",
  "/customer_reservation_menu",
  "/login",
  "/recovery_code",
];

const PUBLIC_PATHS = [
  "/c",
  "/customer_reservation_menu",
  "/login",
  "/recovery_code",
  "/reservations",
  "/widget",
  "/register",
  "/passwordRecovery",
  "/delete_client_info",
  "/notifications",
];


function App() {
  const location = useLocation();
  const id = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isPaddingTop, setIsPaddingTop] = useState(true);
  const [isFooterActive, setIsFooterActive] = useState(true);

  const isReservationActive = useSelector((state) => state.reservationActive);
  const { isActive } = isReservationActive;

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const pathParts = location.pathname.split("/");

  const isWidgetRoute =
    location.pathname.startsWith("/widget") ||
    (location.pathname.startsWith("/reservations") &&
      pathParts[3] === "widget");

  const isPublicRoute = PUBLIC_PATHS.some((path) =>
    location.pathname.startsWith(path),
  );

  const disablePadding = NO_PADDING_PATHS.some((path) =>
    location.pathname.startsWith(path),
  );

  useEffect(() => {
    setIsFooterActive(
      FOOTER_ACTIVE_PATHS.some((path) => location.pathname.startsWith(path)),
    );
  }, [location.pathname]);

  const redirect_to_login = !isPublicRoute;

  useEffect(() => {
    if (!userInfo && redirect_to_login) {
      navigate(`/login/null/null`);
    }
  }, [navigate, userInfo, redirect_to_login]);

  useEffect(() => {
    const verifyUserStillExists = async () => {
      if (!userInfo || isPublicRoute) return;

      try {
        await dispatch(checkCurrentUserExists());
      } catch (error) {
        dispatch(logout(navigate));
      }
    };

    verifyUserStillExists();
  }, [dispatch, navigate, userInfo, isPublicRoute, location.pathname]);

  const hideAppShell = isWidgetRoute || isActive;

  return (
    <div className="App">
      <main>
        {!isWidgetRoute && <ConnectionStatus />}

        <div className="app-shell">
          {hideAppShell ? null : <Header />}

          <StyledRoutesContainer
            noPadding={disablePadding}
            $widgetMode={isWidgetRoute}
          >
            <Routes>
              <Route
                path="/login/:username/:password"
                element={<LoginScreen />}
              />

              <Route
                path="/reservationfromrestaurant/:restaurant_id/:date/:status"
                element={<ReservationFromRestaurantScreen />}
              />

              <Route
                path="/delete_client_info/:token/:phone_number/"
                element={<DeleteClientInfo />}
              />

              <Route
                path="/reservations/:restaurant_id/:from_whom/:token"
                element={<ReservationScreen />}
              />

              <Route
                path="/widget/reservations/:restaurant_id"
                element={<ReservationScreen widgetMode />}
              />

              <Route
                path="/widget/reservations/:restaurant_id/:token"
                element={<ReservationScreen widgetMode />}
              />

              <Route
                path="/RestaurantZonesTablesManagement/:reservation_id"
                element={<RestaurantZonesTablesManagement />}
              />

              <Route
                path="/c/:token/:change_status"
                element={<ReservationCustomersMenuScreen />}
              />

              <Route
                path="/customer_reservation_menu/:token/:change_status"
                element={<ReservationCustomersMenuScreen />}
              />

              <Route
                path="/RestaurantTimeManagement/:restaurant_id"
                element={<RestaurantTimeManagement />}
              />

              <Route path="/time/:restaurant_id" element={<Time />} />

              <Route
                path="/restaurantclients/:restaurant_id"
                element={<RestaurantClients />}
              />

              <Route
                path="/restaurant_creation/:tutorial/:step/:restaurant_id"
                element={<RestaurantCreation />}
              />

              <Route path="/yo" element={<Counter />} />

              <Route
                path="/profile/update/:first_time_manager_login"
                element={<ProfileScreen />}
              />

              <Route path="/register" element={<CreateAccountScreen />} />

              <Route
                path="/manager_account_creation"
                element={<ManagersCreationScreen />}
              />

              <Route
                path="/place_design/:restaurant_id"
                element={<PlaceDesignScreen />}
              />

              <Route
                path="/notifications/:token/:email_phone/:restaurant_name"
                element={<ConfirmNotification />}
              />

              <Route
                path="/restaurant_notifications/:restaurant_id"
                element={<Notifications />}
              />

              <Route
                path="/statistics/:restaurant_id"
                element={<RestaurantStatistics />}
              />

              <Route path="/passwordRecovery" element={<AccountRecovery />} />

              <Route
                path="/recovery_code/:recovery_code"
                element={<LoginScreen />}
              />

              <Route path="/FiscalAddress" element={<FiscalAdress />} />

              <Route
                path="/pick_plan_and_pay/:restaurant_id"
                element={<ChoosePlanAndPay />}
              />

              <Route
                path="/restaurant_info_update/:restaurant_id"
                element={<RestaurantInfoUpdate />}
              />

              <Route
                path="/restaurant_widget/:restaurant_id"
                element={<RestaurantWidgetSettings />}
              />

              <Route
                path="/restaurantsettingsupdate/:restaurant_id"
                element={<RestaurantSettingsUpdate />}
              />

              <Route
                path="/subscription_management/:restaurant_id"
                element={<SubscriptionManagement />}
              />

              <Route path="/" element={<HomeScreen />} />
            </Routes>

            {isFooterActive && !isWidgetRoute ? <Footer /> : null}
          </StyledRoutesContainer>
        </div>
        {!hideAppShell && <RestaurantSetupTour />}
      </main>
    </div>
  );
}

export default App;
